import React, { useMemo } from 'react';
import './TitulacionBars.css';

interface BarValue {
  metric: string;
  label: string;
  values: Record<string, number>;
}

interface CourseInfo {
  course: string;
  label: string;
}

interface Scope {
  kind: string;
  label: string;
}

export interface TitulacionBarsData {
  type: 'titulacion-bars' | 'faculty-bars';
  title: string;
  subtitle?: string;
  courses: CourseInfo[];
  bars: BarValue[];
  scope?: Scope;
  question?: string;
  metric?: string;
  metricLabel?: string;
}

const COURSE_COLORS = ['#7be495', '#5b8fb9', '#f6c85f', '#c490d1', '#6dd3e3', '#fb923c'];

const TitulacionBars: React.FC<{ data: TitulacionBarsData }> = ({ data }) => {
  const { title, subtitle, courses, bars, scope } = data;

  const maxValue = useMemo(() => {
    let max = 0;
    for (const bar of bars) {
      for (const val of Object.values(bar.values)) {
        if (val > max) max = val;
      }
    }
    return max || 1;
  }, [bars]);

  const scopeLabel = useMemo(() => {
    if (data.metricLabel) return data.metricLabel.toUpperCase();
    if (!scope?.kind) return '';
    const kindMap: Record<string, string> = {
      titulacion: 'TITULACIÓN',
      faculty: 'FACULTAD',
      universidad: 'UNIVERSIDAD',
      campus: 'CAMPUS',
      breakdown: 'DESGLOSE',
    };
    return kindMap[scope.kind.toLowerCase()] || scope.kind.toUpperCase();
  }, [scope, data.metricLabel]);

  const orderedCourses = useMemo(() => {
    return [...courses].sort((a, b) => b.course.localeCompare(a.course));
  }, [courses]);

  return (
    <div className="titulacion-bars">
      <div className="tb-header">
        {scopeLabel && <span className="tb-scope-label">{scopeLabel}</span>}
        <h2 className="tb-title">{scope?.label || title}</h2>
        {subtitle && <p className="tb-subtitle">{subtitle}</p>}
      </div>

      <div className="tb-legend">
        {orderedCourses.map((course, idx) => (
          <span className="tb-legend-item" key={course.course}>
            <span
              className="tb-legend-swatch"
              style={{ background: COURSE_COLORS[idx % COURSE_COLORS.length] }}
            />
            {course.label}
          </span>
        ))}
      </div>

      <div className="tb-metrics">
        {bars.map((bar) => {
          const currentCourse = orderedCourses[0];
          const refCourse = orderedCourses.length > 1 ? orderedCourses[1] : null;
          const currentVal = bar.values[currentCourse?.course] ?? 0;
          const refVal = refCourse ? (bar.values[refCourse.course] ?? 0) : null;

          let changePct = 0;
          let changeDir: 'up' | 'down' | 'neutral' = 'neutral';
          if (refVal && refVal > 0) {
            changePct = ((currentVal - refVal) / refVal) * 100;
            changeDir = changePct > 0 ? 'up' : changePct < 0 ? 'down' : 'neutral';
          }

          return (
            <div className="tb-metric-group" key={bar.metric}>
              <div className="tb-metric-header">
                <span className="tb-metric-label">{bar.label || bar.metric}</span>
                {refVal !== null && (
                  <span className={`tb-change-badge tb-change-${changeDir}`}>
                    {changeDir === 'up' ? '▲' : changeDir === 'down' ? '▼' : ''}
                    {changePct >= 0 ? '+' : ''}{changePct.toFixed(1)} %
                  </span>
                )}
              </div>
              <div className="tb-bar-rows">
                {orderedCourses.map((course, idx) => {
                  const val = bar.values[course.course] ?? 0;
                  const widthPct = (val / maxValue) * 100;
                  return (
                    <div className="tb-bar-row" key={course.course}>
                      <span className="tb-bar-course-label">{course.label}</span>
                      <div className="tb-bar-track">
                        <div
                          className="tb-bar-fill"
                          style={{
                            width: `${widthPct}%`,
                            background: COURSE_COLORS[idx % COURSE_COLORS.length],
                          }}
                        />
                      </div>
                      <span className="tb-bar-value">{val.toLocaleString('es-ES')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TitulacionBars;
